import useSWR from "swr";
import { Panel, PanelHeader } from "@/components/common/Card";
import { Pill, DirectionPill } from "@/components/common/Pill";
import { getRejected, formatNum, timeAgo } from "@/lib/api";
import { REJECTED } from "@/constants/testIds";
import { XCircle, Funnel } from "@phosphor-icons/react";

const STRAT_LABEL = {
  trend_pullback: "Trend Pullback",
  breakout_retest: "Breakout Retest",
  range_trading: "Range Trading",
  liquidity_sweep: "Liquidity Sweep",
};

export const Rejected = () => {
  const { data: rejected, isLoading } = useSWR(
    "/rejected-signals",
    () => getRejected(200),
    { refreshInterval: 20000 },
  );

  const rows = rejected || [];

  return (
    <div className="flex flex-col gap-6 max-w-[1600px]">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-slate-500 font-bold">
            Filter Reports
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-black tracking-tighter text-slate-900 mt-1">
            Rejected Signals
          </h1>
          <div className="text-xs text-slate-500 mt-2">
            Setups detected by the engine but blocked by scoring, risk, or
            regime filters.
          </div>
        </div>
        <div className="text-xs text-slate-500 font-data">
          {rows.length} results · auto-refresh 20s
        </div>
      </div>

      <Panel>
        <PanelHeader
          title="Rejection Log"
          hint="Newest first · grouped by pair"
          action={<Funnel size={14} className="text-slate-500" />}
        />
        <div className="overflow-x-auto">
          <table
            data-testid={REJECTED.table}
            className="w-full text-sm"
          >
            <thead>
              <tr className="text-[10px] tracking-[0.1em] uppercase text-slate-500 border-b border-slate-200/70">
                <th className="text-left font-bold px-4 py-2.5">Pair</th>
                <th className="text-left font-bold px-4 py-2.5">Dir</th>
                <th className="text-left font-bold px-4 py-2.5">Strategy</th>
                <th className="text-left font-bold px-4 py-2.5">Regime</th>
                <th className="text-right font-bold px-4 py-2.5">Score</th>
                <th className="text-right font-bold px-4 py-2.5">Entry</th>
                <th className="text-right font-bold px-4 py-2.5">SL</th>
                <th className="text-right font-bold px-4 py-2.5">RR</th>
                <th className="text-left font-bold px-4 py-2.5">Rejection Reasons</th>
                <th className="text-right font-bold px-4 py-2.5">Time</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-10 text-center text-xs text-slate-400"
                  >
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={10}
                    className="px-4 py-12 text-center text-xs text-slate-400"
                  >
                    <XCircle
                      size={28}
                      className="mx-auto mb-2 text-slate-400"
                    />
                    No rejected setups yet — the scanner hasn’t produced
                    enough candidates.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={REJECTED.row(r.id)}
                    className="border-b border-slate-200/70 hover:bg-[#e2e8f0] transition-colors align-top"
                  >
                    <td className="px-4 py-3 font-data text-slate-900 whitespace-nowrap">
                      {r.pair}
                    </td>
                    <td className="px-4 py-3">
                      <DirectionPill direction={r.direction} />
                    </td>
                    <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">
                      {STRAT_LABEL[r.strategy] || r.strategy}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-[11px] tracking-wide whitespace-nowrap">
                      {r.market_regime}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-[#d97706]">
                      {formatNum(r.score, 1)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-slate-700">
                      {formatNum(r.entry)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-[#dc2626]">
                      {formatNum(r.stop_loss)}
                    </td>
                    <td className="px-4 py-3 text-right font-data text-slate-600">
                      {r.rr != null ? formatNum(r.rr, 2) : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5 max-w-[480px]">
                        {(r.rejection_reasons || []).length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          r.rejection_reasons.map((reason, i) => (
                            <Pill
                              key={i}
                              tone="error"
                              data-testid={`rejection-reason-${r.id}-${i}`}
                            >
                              <XCircle size={10} weight="fill" />
                              {reason}
                            </Pill>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-500 text-xs whitespace-nowrap">
                      {timeAgo(r.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};
